import {useRoute, useRouter} from 'vue-router'
import {isNavigationFailure, NavigationFailureType} from 'vue-router'
import {openUrl} from '@/helpers/url.js'
import {computed, isRef, unref} from 'vue'

/**
 * Routing composable
 */
export function useRouting() {

  //Get route and router
  const route = useRoute()
  const router = useRouter()
  const routeMeta = computed(() => route?.meta)
  const routeParams = computed(() => route?.params)
  const queryParams = computed(() => route?.query)

  //Check if param is empty
  const isParamEmpty = param => (param === null || param === undefined || param === '')

  //Filter out undefined params
  const cleanParams = params => {
    const clean = {}
    for (const key in params) {
      const value = isRef(params[key]) ? unref(params[key]) : params[key]
      if (!isParamEmpty(value)) {
        clean[key] = value
      }
    }
    return clean
  }

  //Ensure route is an object
  const ensureObject = route => {
    if (typeof route === 'string') {
      return {name: route}
    }
    else if (route && typeof route === 'object') {
      return route
    }
    throw new Error(`Invalid route parameter provided: ${route}`)
  }

  //With query params
  const withQuery = (route, query) => {
    route = ensureObject(route)
    return {...route, query: cleanParams(query)}
  }

  //With router params
  const withParams = (route, params) => {
    route = ensureObject(route)
    return {...route, params: cleanParams(params)}
  }

  //Handle routing error
  const handleRoutingError = error => {
    if (isNavigationFailure(error, NavigationFailureType.duplicated)) {
      console.log('Duplicate route error', error)
      return
    }
    if (isNavigationFailure(error, NavigationFailureType.aborted)) {
      console.log('Aborted route error', error)
      return
    }
    throw error
  }

  //Push or replace a route, with handling of duplicated route errors
  const pushRoute = async (route, event) => {

    //Ensure route object
    route = ensureObject(route)

    //Event provided, check if we want to open this link/route in a new tab
    if (event?.ctrlKey || event?.metaKey) {
      openUrl(router.resolve(route).href, null, true)
      return
    }

    //Go to route directly
    try {
      await router.push(route)
    }
    catch (error) {
      console.log('Handling routing error for pushRoute', error)
      handleRoutingError(error)
    }
  }
  const replaceRoute = async (route) => {
    route = ensureObject(route)
    try {
      await router.replace(route)
    }
    catch (error) {
      console.log('Handling routing error for replaceRoute', error)
      handleRoutingError(error)
    }
  }

  //Replace current route convenience helper
  const replaceCurrentRoute = params => replaceRoute({name: route.name, params})

  //Check if we're currently on a specified route
  const isRouteActive = (name, params) => {
    if (!name) {
      return false
    }
    if (!route.matched.some(match => match.name === name)) {
      return false
    }
    if (params && typeof params !== 'object') {
      for (const key in params) {
        if (routeParams.value[key] !== params[key]) {
          return false
        }
      }
    }
    return true
  }

  //Expose interface
  return {
    router,
    route,
    routeMeta,
    routeParams,
    queryParams,
    isRouteActive,
    pushRoute,
    replaceRoute,
    replaceCurrentRoute,
    withQuery,
    withParams,
    cleanParams,
    handleRoutingError,
  }
}
